<template>
<div class="relative" ref="wrapper">
    <div class="flex items-center border p-2 rounded">
        <input type="text" class="flex-1" ref="inputEl"
            :placeholder="placeholder"
            :disabled="!!modelValue"
                v-model="search"
                    @input="handleSearch"
                    @focus="openDropdown" />
        <span class="material-symbols-outlined animate-spin" v-if="search_started">cached</span>
        <button type="button" class="text-gray-400 hover:text-black" @click="clear" v-if="modelValue">{{ gettext("✕") }}</button>
    </div>
    <Teleport to="body">
    <div class="z-1000 bg-white border rounded shadow max-h-48 overflow-auto" :style="dropdownStyle" v-if="isOpen && results.length">
        <div class="p-2 hover:bg-gray-100 cursor-pointer" v-for="(item, i) in results" :key="i" @mousedown.prevent="select(item)">
            <div class="font-semibold">{{ getTitle(item) }}</div>
            <div class="text-sm text-gray-500">{{ getSubtitle(item) }}</div>
        </div>
    </div>
    </Teleport>
</div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
    modelValue: [String, Number, Object],
    api: Function, // async (query) => Promise<{ results: [] }>
    options: {type: Array, default: () => [] },
    titleKey: {type: String, default: 'label'},
    subtitleKey: {type: String, default: '' },
    valueKey: {type: String, default: 'id'},
    resultsKey: {type: String, default: 'results'},
    placeholder: {type: String, default: 'Rechercher...'},
})

const emit = defineEmits(['update:modelValue', 'update:object'])
const search = ref('')
const isOpen = ref(false)
const results = ref([])
const debounceTimer = ref(null)
const wrapper = ref(null)
const inputEl = ref(null)
const dropdownStyle = ref({})
const search_started = ref(false)
const last_query = ref(null);

function getTitle(item) { return item?.[props.titleKey] ?? item }
function getSubtitle(item) { return props.subtitleKey ? item?.[props.subtitleKey] ?? '' : '' }
function select(item) {
    emit('update:modelValue', item?.[props.valueKey])
    emit('update:object', item)
    search.value = getTitle(item)
    isOpen.value = false
}


function put_on_hold(callback, timer = 1000) {
    if (debounceTimer.value) {
        clearTimeout(debounceTimer.value)
    }
    debounceTimer.value = setTimeout(() => {
        callback?.()
    }, timer)
}

 function handleSearch() {
    const query = search.value;

    if (last_query.value === query && results.value.length > 0) {
        return;
    }
    last_query.value = query;
    search_started.value = true;

    put_on_hold(async () => {
      if (props.api) {
          try {
                const res = await props.api(query);
                results.value = Array.isArray(res) ? res : res?.[props.resultsKey] || [];
                search_started.value = false;
            } catch (e) {
                console.error('Erreur API CustomSelect', e)
                results.value = []
                search_started.value = false;
            }
        } else {
            results.value = props.options.filter(opt => getTitle(opt).toLowerCase().includes(query.toLowerCase()))
            search_started.value = false;
        }
    }, 500);
}

function openDropdown() {
  isOpen.value = true
  handleSearch()
  nextTick(() => {
    if (!inputEl.value) return
    const rect = inputEl.value.getBoundingClientRect()
    dropdownStyle.value = {
      position: 'fixed',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    }
  })
}

function clear() {
    emit('update:modelValue', null)
    search.value = ''
    results.value = []
}

function handleClickOutside(e) {
    if (wrapper.value && !wrapper.value.contains(e.target)) {
      isOpen.value = false
    }
}

onMounted(() => { document.addEventListener('click', handleClickOutside) })
onBeforeUnmount(() => { document.removeEventListener('click', handleClickOutside) })

// Initialiser visuellement le champ avec la valeur sélectionnée
watch(
  () => props.modelValue,
    async (val) => {
    if (!val) {
      search.value = ''
      last_query.value = null;
      return
    }

    // Si on a déjà un objet complet (pas juste l'ID), pas besoin de fetch
    if (typeof val === 'object' && val !== null) {
      search.value = getTitle(val)
      return
    }

    const findIn = (list) => list.find(item => item?.[props.valueKey] == val)

    // D’abord, on cherche dans les options locales
    let match = findIn(results.value) || findIn(props.options)

    // Si on ne trouve rien, on fait une requête API
    if (!match && props.api) {
      try {
        const res = await props.api('') // ou props.api(val) si API accepte les ID
        const all = Array.isArray(res) ? res : res?.[props.resultsKey] || []
        match = findIn(all)
      } catch (e) {
        console.error('Erreur API CustomSelect', e)
      }
    }

    if (match) {
      search.value = getTitle(match)
    }
  },
  { immediate: true }
)
</script>
